'use client';

/**
 * Detects that a mounted widget's bundle was reseeded/republished AFTER the
 * iframe loaded, so long-lived tabs can offer an in-place update instead of
 * silently running weeks-old app code (a widget iframe never remounts on SPA
 * navigation, and nothing else tells the user a reseed happened).
 *
 * Two signals, both read through ONE batched `/api/content/info-batch` call
 * for every registered widget on window focus / tab-visible, throttled to
 * once per interval (no polling while the tab sits in the background):
 *
 *   - `updatedAt` — catalog rows: the seeder and the publish flow rewrite the
 *     row on every bundle change. Baseline is captured at mount (matching the
 *     iframe's own load); anything newer later flips `stale`.
 *   - `latestVersionContentId` — VERSION-PINNED rows (marketplace spawns
 *     reference an immutable published-version artifact whose `updatedAt`
 *     never changes): the server advertises the successor artifact when one
 *     exists. Surfaced as `latest` for the caller to compare against what the
 *     instance already runs (`state.appVersionContentId`) — supersededness is
 *     absolute, not relative to mount, so no baseline is involved.
 */
import { useCallback, useEffect, useState } from 'react';

const CHECK_MIN_INTERVAL_MS = 5 * 60_000;
/** Collapse the mount burst (a canvas mounts many widgets at once) into one baseline fetch. */
const MOUNT_DEBOUNCE_MS = 800;

type FreshnessUpdate = { stale?: boolean; latest?: string | null };

const listeners = new Map<string, Set<(u: FreshnessUpdate) => void>>();
const baselines = new Map<string, string>();
const latestById = new Map<string, string | null>();
let lastCheck = 0;
let inflight = false;
let wired = false;
let mountTimer: ReturnType<typeof setTimeout> | null = null;

async function check(force: boolean): Promise<void> {
  const now = Date.now();
  if (inflight) return;
  if (!force && now - lastCheck < CHECK_MIN_INTERVAL_MS) return;
  const ids = [...listeners.keys()].slice(0, 100);
  if (ids.length === 0) return;
  inflight = true;
  lastCheck = now;
  try {
    const res = await fetch('/api/content/info-batch', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) return;
    const body = (await res.json()) as {
      infos?: Record<string, { updatedAt?: string | null; latestVersionContentId?: string | null } | null>;
    };
    for (const id of ids) {
      const info = body.infos?.[id];
      if (!info) continue;
      const latest = info.latestVersionContentId ?? null;
      if (latestById.get(id) !== latest) {
        latestById.set(id, latest);
        for (const cb of listeners.get(id) ?? []) cb({ latest });
      }
      const at = info.updatedAt ?? null;
      if (!at) continue;
      const base = baselines.get(id);
      // First observation IS the baseline — taken right after the iframe
      // loaded, so anything newer later is a real post-mount update.
      if (!base) {
        baselines.set(id, at);
        continue;
      }
      if (at > base) for (const cb of listeners.get(id) ?? []) cb({ stale: true });
    }
  } catch {
    // Offline / transient — the next focus event retries.
  } finally {
    inflight = false;
  }
}

function wire(): void {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  const onWake = () => {
    if (document.visibilityState === 'visible') void check(false);
  };
  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onWake);
}

/**
 * `stale` flips true when the widget's catalog row updated after mount.
 * `latest` carries the server-advertised successor artifact for a
 * version-pinned row (null when current). `markFresh(...)` re-baselines
 * after the caller remounts the frame.
 */
export function useWidgetFreshness(contentId: string | null | undefined): {
  stale: boolean;
  latest: string | null;
  markFresh: () => void;
} {
  const [stale, setStale] = useState(false);
  const [latest, setLatest] = useState<string | null>(
    () => (contentId ? latestById.get(contentId) ?? null : null),
  );
  useEffect(() => {
    if (!contentId) return;
    wire();
    const onUpdate = (u: FreshnessUpdate) => {
      if (u.stale !== undefined) setStale(u.stale);
      if (u.latest !== undefined) setLatest(u.latest);
    };
    let set = listeners.get(contentId);
    if (!set) listeners.set(contentId, (set = new Set()));
    set.add(onUpdate);
    setLatest(latestById.get(contentId) ?? null);
    if (!baselines.has(contentId)) {
      if (mountTimer) clearTimeout(mountTimer);
      mountTimer = setTimeout(() => void check(true), MOUNT_DEBOUNCE_MS);
    }
    return () => {
      set?.delete(onUpdate);
      if (set && set.size === 0) listeners.delete(contentId);
    };
  }, [contentId]);
  const markFresh = useCallback(() => {
    if (!contentId) return;
    // The remounted frame is now as fresh as the server — client-now is a
    // safe new baseline (updatedAt it just caught up to is in the past).
    baselines.set(contentId, new Date().toISOString());
    setStale(false);
  }, [contentId]);
  return { stale, latest, markFresh };
}
